<template>
  <div class="recouvrement-creances-manager">
    <!-- Header avec statistiques -->
    <div class="section-header">
      <div class="header-info">
        <h3><i class="fas fa-hand-holding-usd"></i> Espace Recouvrement</h3>
        <p>Gestion des créances clients</p>
      </div>
      <div class="header-stats">
        <div class="stat-card total">
          <div class="stat-value">{{ formatCurrency(statistiques.totalGlobal) }}</div>
          <div class="stat-label">Total Créances</div>
        </div>
        <div class="stat-card clients">
          <div class="stat-value">{{ nombreClients }}</div>
          <div class="stat-label">Clients</div>
        </div>
      </div>
    </div>

    <!-- Onglets Vue -->
    <div class="view-tabs">
      <button 
        @click="activeView = 'globale'"
        :class="['tab-btn', { active: activeView === 'globale' }]"
      >
        <i class="fas fa-globe"></i>
        Vue Globale
      </button>
      <button 
        @click="activeView = 'detail'"
        :class="['tab-btn', { active: activeView === 'detail' }]"
      >
        <i class="fas fa-user"></i>
        Détail Client
      </button>
    </div>

    <!-- VUE GLOBALE -->
    <div v-if="activeView === 'globale'" class="view-content">
      <div class="filters-bar">
        <div class="filter-group">
          <input 
            v-model="filterClient" 
            type="text" 
            placeholder="Rechercher un client..."
            class="filter-input"
          >
          <button 
            @click="afficherClientsSoldes = !afficherClientsSoldes; loadVueGlobale()"
            :class="['btn-toggle-soldes', { active: afficherClientsSoldes }]"
            :title="afficherClientsSoldes ? 'Masquer les clients soldés' : 'Afficher les clients soldés'"
          >
            <i :class="afficherClientsSoldes ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            {{ afficherClientsSoldes ? 'Masquer soldés' : 'Afficher soldés' }}
          </button>
          <button @click="loadVueGlobale" class="btn-refresh">
            <i class="fas fa-sync-alt"></i>
          </button>
          <button @click="exportVueGlobaleExcel" class="btn-export-global">
            <i class="fas fa-file-excel"></i>
            Export
          </button>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
      </div>

      <div v-else class="table-container">
        <table class="creances-table">
          <thead>
            <tr>
              <th>Client</th>
              <th class="text-right">Prix Total Opérations</th>
              <th class="text-right">Total Avance</th>
              <th class="text-right">Reste Créance</th>
              <th>Action</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="item in filteredCreancesGlobale" 
              :key="item.telephone"
              class="creance-row"
            >
              <td>
                <div class="client-cell">
                  <strong>{{ item.client }}</strong>
                  <span class="telephone">{{ item.telephone }}</span>
                </div>
              </td>
              <td class="text-right">{{ formatCurrency(item.totalOperations) }}</td>
              <td class="text-right text-success">{{ formatCurrency(item.totalAvance) }}</td>
              <td class="text-right text-danger font-bold">{{ formatCurrency(item.totalCreance) }}</td>
              <td>
                <button 
                  @click="voirDetailClient(item.telephone)"
                  class="btn-voir"
                >
                  <i class="fas fa-eye"></i>
                  Voir
                </button>
              </td>
              <td>
                <input 
                  type="text" 
                  v-model="item.note"
                  placeholder="Écrire une note..."
                  class="note-input"
                  @blur="sauvegarderNote(item)"
                >
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td><strong>TOTAL</strong></td>
              <td class="text-right">{{ formatCurrency(totalOperationsGlobal) }}</td>
              <td class="text-right text-success">{{ formatCurrency(totalAvanceGlobal) }}</td>
              <td class="text-right text-danger font-bold">{{ formatCurrency(statistiques.totalGlobal) }}</td>
              <td colspan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- VUE DÉTAIL CLIENT -->
    <div v-if="activeView === 'detail'" class="view-content">
      <div class="client-selector">
        <label>Sélectionner un client:</label>
        <div class="searchable-select">
          <div class="search-input-wrapper">
            <i class="fas fa-search"></i>
            <input 
              v-model="searchDetailClient"
              type="text"
              placeholder="Rechercher par nom ou téléphone..."
              class="search-input"
              @focus="showDetailDropdown = true"
              @input="showDetailDropdown = true"
            >
            <button 
              v-if="selectedClientId" 
              @click="clearDetailSelection"
              class="clear-btn"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div v-if="showDetailDropdown && filteredDetailClients.length > 0" class="dropdown-list">
            <div 
              v-for="item in filteredDetailClients" 
              :key="item.telephone"
              class="dropdown-item"
              @click="selectDetailClient(item)"
            >
              <div class="item-name">{{ item.client }}</div>
              <div class="item-details">
                <span class="item-phone"><i class="fas fa-phone"></i> {{ item.telephone }}</span>
                <span class="item-amount text-danger">{{ formatCurrency(item.totalCreance) }}</span>
              </div>
            </div>
          </div>
          <div v-if="showDetailDropdown && searchDetailClient && filteredDetailClients.length === 0" class="dropdown-empty">
            Aucun client trouvé
          </div>
        </div>
      </div>

      <div v-if="selectedClient" class="client-detail">
        <!-- Résumé client -->
        <div class="client-summary">
          <div class="summary-header">
            <h4>{{ selectedClient.nomComplet }}</h4>
            <p><i class="fas fa-phone"></i> {{ selectedClient.telephone }}</p>
          </div>
          <div class="summary-stats">
            <div class="stat">
              <span class="label">Reste à payer</span>
              <span class="value danger">{{ formatCurrency(clientTotaux.totalCreance) }}</span>
            </div>
          </div>
        </div>

        <!-- Accordéon Créances Client -->
        <div class="accordion-section">
          <div 
            class="accordion-header" 
            :class="{ 'active': accordionCreances }"
            @click="accordionCreances = !accordionCreances"
          >
            <div class="accordion-title">
              <i class="fas fa-file-invoice-dollar"></i>
              <span>Créances du client</span>
              <span class="badge-count">{{ clientCreances.length }}</span>
            </div>
            <div class="accordion-actions">
              <button 
                v-if="accordionCreances && clientCreances.length > 0"
                @click.stop="exportCreancesExcel"
                class="btn-export"
              >
                <i class="fas fa-file-excel"></i>
                Export
              </button>
              <i class="fas fa-chevron-down accordion-icon"></i>
            </div>
          </div>
          <div class="accordion-content" v-show="accordionCreances">
            <div class="detail-table-container">
              <table class="detail-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Technicien</th>
                    <th>Opération</th>
                    <th class="text-right">Total</th>
                    <th class="text-right">Avance</th>
                    <th class="text-right">Date Avance</th>
                    <th>Mode</th>
                    <th class="text-right">Reste</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="creance in clientCreances" :key="creance._id">
                    <td>{{ formatDate(creance.dateIntervention) }}</td>
                    <td>{{ creance.technicien?.prenom }} {{ creance.technicien?.nom }}</td>
                    <td>{{ creance.typeIntervention || 'Installation' }}</td>
                    <td class="text-right">{{ formatCurrency(creance.prixMission) }}</td>
                    <td class="text-right text-success">{{ formatCurrency(creance.montantPaye) }}</td>
                    <td class="text-right">{{ formatDate(creance.dateCreation) }}</td>
                    <td>
                      <span class="mode-badge">{{ getModeFromRdv(creance) }}</span>
                    </td>
                    <td class="text-right text-danger font-bold">{{ formatCurrency(creance.resteAPayer) }}</td>
                    <td>
                      <button 
                        v-if="creance.resteAPayer > 0"
                        @click="openPaiementModal(creance)"
                        class="btn-paiement"
                      >
                        <i class="fas fa-money-bill-wave"></i>
                        Paiement
                      </button>
                      <span v-else class="badge-solde">Soldé</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Accordéon Historique paiements -->
        <div v-if="clientPaiements.length > 0" class="accordion-section">
          <div 
            class="accordion-header" 
            :class="{ 'active': accordionPaiements }"
            @click="accordionPaiements = !accordionPaiements"
          >
            <div class="accordion-title">
              <i class="fas fa-history"></i>
              <span>Historique des paiements</span>
              <span class="badge-count">{{ clientPaiements.length }}</span>
            </div>
            <div class="accordion-actions">
              <button 
                v-if="accordionPaiements && clientPaiements.length > 0"
                @click.stop="exportPaiementsExcel"
                class="btn-export"
              >
                <i class="fas fa-file-excel"></i>
                Export
              </button>
              <i class="fas fa-chevron-down accordion-icon"></i>
            </div>
          </div>
          <div class="accordion-content" v-show="accordionPaiements">
            <div class="paiements-table-container">
              <table class="paiements-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th class="text-right">Montant</th>
                    <th>Mode</th>
                    <th>Agent</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="paiement in clientPaiements" 
                    :key="paiement._id"
                    :class="[getStatutClass(paiement.statut), { 'avance-row': paiement.isAvanceInitiale }]"
                  >
                    <td>{{ formatDate(paiement.datePaiement) }}</td>
                    <td>
                      <span v-if="paiement.isAvanceInitiale" class="type-badge avance">
                        <i class="fas fa-hand-holding-usd"></i> Avance
                      </span>
                      <span v-else class="type-badge paiement">
                        <i class="fas fa-money-check"></i> Paiement
                      </span>
                    </td>
                    <td class="text-right text-success font-bold">{{ formatCurrency(paiement.montant) }}</td>
                    <td>
                      <span class="mode-badge">{{ paiement.modePaiement }}</span>
                    </td>
                    <td>
                      <span class="agent-name">
                        {{ paiement.collectePar?.prenom || '-' }} {{ paiement.collectePar?.nom || '' }}
                      </span>
                    </td>
                    <td>
                      <span class="statut-badge" :class="getStatutClass(paiement.statut)">
                        {{ paiement.statut }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL PAIEMENT CRÉANCE -->
    <div v-if="showPaiementModal" class="modal-overlay" @click.self="closePaiementModal">
      <div class="paiement-modal">
        <div class="modal-header">
          <h4><i class="fas fa-money-check-alt"></i> Paiement Créance</h4>
          <button @click="closePaiementModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Info client -->
          <div class="client-info-modal">
            <div class="client-name">
              <i class="fas fa-user"></i>
              {{ paiementForm.clientName }}
            </div>
            <div class="client-phone">
              <i class="fas fa-phone"></i>
              {{ paiementForm.clientId }}
            </div>
          </div>

          <div class="form-group">
            <label>Reste à payer</label>
            <div class="reste-display">{{ formatCurrency(paiementForm.resteAPayer) }}</div>
          </div>

          <div class="form-group">
            <label>Montant à payer *</label>
            <input 
              type="number" 
              v-model.number="paiementForm.montant"
              placeholder="Entrer le montant..."
              class="form-control"
              :max="paiementForm.resteAPayer"
            >
          </div>

          <div class="form-group">
            <label>Mode de paiement *</label>
            <select v-model="paiementForm.modePaiement" class="form-control">
              <option value="">-- Sélectionner --</option>
              <option value="Espèces">Espèces</option>
              <option value="Chèque">Chèque</option>
              <option value="Virement">Virement</option>
              <option value="CB">Carte Bancaire</option>
            </select>
          </div>

          <div class="form-group">
            <label>Référence (optionnel)</label>
            <input 
              type="text" 
              v-model="paiementForm.reference"
              placeholder="N° chèque, référence virement..."
              class="form-control"
            >
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closePaiementModal" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Annuler
          </button>
          <button 
            @click="showConfirmationModal"
            :disabled="!canSubmitPaiement"
            class="btn btn-primary"
          >
            <i class="fas fa-check"></i>
            Valider le paiement
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL CONFIRMATION PAIEMENT -->
    <div v-if="showConfirmModal" class="modal-overlay confirmation-overlay" @click.self="cancelConfirmation">
      <div class="confirmation-modal">
        <div class="confirmation-icon">
          <i class="fas fa-question-circle"></i>
        </div>
        <h4 class="confirmation-title">Confirmer le paiement</h4>
        <p class="confirmation-message">
          Valider ce paiement ?<br>
          <span class="highlight">Le reste à payer sera mis à jour.</span>
        </p>
        <div class="confirmation-amount">
          <span class="amount-label">Montant:</span>
          <span class="amount-value">{{ formatCurrency(paiementForm.montant) }}</span>
        </div>
        <div class="confirmation-buttons">
          <button @click="cancelConfirmation" class="btn-cancel">
            <i class="fas fa-times"></i>
            Annuler
          </button>
          <button @click="confirmPaiement" class="btn-confirm">
            <i class="fas fa-check"></i>
            Confirmer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import api from '@/services/api'

export default {
  name: 'RecouvrementCreancesManager',
  props: {
    userRole: String
  },
  setup() {
    const toast = useToast()
    const loading = ref(false)
    const activeView = ref('globale')
    
    // Vue globale
    const creancesGlobale = ref([])
    const filterClient = ref('')
    const statistiques = ref({ totalGlobal: 0 })
    const afficherClientsSoldes = ref(true) // Afficher les clients soldés par défaut
    
    // Vue détail
    const selectedClientId = ref('')
    const selectedClient = ref(null)
    const clientCreances = ref([])
    const clientPaiements = ref([])
    const clientTotaux = ref({ totalOperations: 0, totalAvance: 0, totalCreance: 0 })
    
    // Recherche clients
    const searchDetailClient = ref('')
    const showDetailDropdown = ref(false)
    const searchPaiementClient = ref('')
    const showPaiementDropdown = ref(false)
    
    // Modal paiement
    const showPaiementModal = ref(false)
    
    // Modal confirmation
    const showConfirmModal = ref(false)
    
    // Accordéons
    const accordionCreances = ref(false)
    const accordionPaiements = ref(false)
    
    // Formulaire paiement
    const paiementForm = ref({
      clientId: '',
      clientName: '',
      creanceId: '',
      montant: 0,
      modePaiement: '',
      reference: '',
      resteAPayer: 0
    })

    const nombreClients = computed(() => creancesGlobale.value.length)
    
    const totalOperationsGlobal = computed(() => 
      creancesGlobale.value.reduce((sum, c) => sum + c.totalOperations, 0)
    )
    
    const totalAvanceGlobal = computed(() => 
      creancesGlobale.value.reduce((sum, c) => sum + c.totalAvance, 0)
    )

    const filteredCreancesGlobale = computed(() => {
      if (!filterClient.value) return creancesGlobale.value
      const search = filterClient.value.toLowerCase()
      return creancesGlobale.value.filter(c => 
        c.client?.toLowerCase().includes(search) ||
        c.telephone?.includes(search)
      )
    })

    const canSubmitPaiement = computed(() => {
      return paiementForm.value.clientId &&
             paiementForm.value.montant > 0 &&
             paiementForm.value.montant <= paiementForm.value.resteAPayer &&
             paiementForm.value.modePaiement
    })

    // Filtres pour les recherches de clients
    const filteredDetailClients = computed(() => {
      if (!searchDetailClient.value) return creancesGlobale.value
      const search = searchDetailClient.value.toLowerCase()
      return creancesGlobale.value.filter(c => 
        c.client?.toLowerCase().includes(search) ||
        c.telephone?.includes(search)
      )
    })

    const filteredPaiementClients = computed(() => {
      if (!searchPaiementClient.value) return creancesGlobale.value
      const search = searchPaiementClient.value.toLowerCase()
      return creancesGlobale.value.filter(c => 
        c.client?.toLowerCase().includes(search) ||
        c.telephone?.includes(search)
      )
    })

    const loadVueGlobale = async () => {
      loading.value = true
      try {
        const [globaleRes, statsRes] = await Promise.all([
          api.get('/creances/globale', {
            params: {
              afficherSoldes: afficherClientsSoldes.value
            }
          }),
          api.get('/creances/statistiques')
        ])
        creancesGlobale.value = globaleRes.data.creancesParClient || []
        statistiques.value = statsRes.data.statistiques || { totalGlobal: 0 }
      } catch (error) {
        console.error('Erreur chargement:', error)
        toast.error('Erreur lors du chargement')
      } finally {
        loading.value = false
      }
    }

    const loadDetailClient = async () => {
      if (!selectedClientId.value) {
        selectedClient.value = null
        clientCreances.value = []
        clientPaiements.value = []
        return
      }
      
      try {
        const response = await api.get(`/creances/client/${selectedClientId.value}`)
        selectedClient.value = response.data.client
        clientCreances.value = response.data.creances || []
        clientPaiements.value = response.data.paiements || []
        clientTotaux.value = response.data.totaux || {}
      } catch (error) {
        console.error('Erreur détail client:', error)
        toast.error('Erreur lors du chargement')
      }
    }

    const loadCreancesClient = async () => {
      if (!paiementForm.value.clientId) {
        paiementForm.value.resteAPayer = 0
        return
      }
      
      try {
        const response = await api.get(`/creances/client/${paiementForm.value.clientId}`)
        paiementForm.value.resteAPayer = response.data.totaux?.totalCreance || 0
        
        // Prendre la première créance active
        const creanceActive = response.data.creances?.find(c => 
          ['En recouvrement', 'Partiellement payée'].includes(c.statut)
        )
        if (creanceActive) {
          paiementForm.value.creanceId = creanceActive._id
        }
      } catch (error) {
        console.error('Erreur:', error)
      }
    }

    const voirDetailClient = (clientId) => {
      selectedClientId.value = clientId
      const client = creancesGlobale.value.find(c => c.telephone === clientId)
      if (client) {
        searchDetailClient.value = client.client
      }
      activeView.value = 'detail'
      showDetailDropdown.value = false
      loadDetailClient()
    }

    const selectDetailClient = (item) => {
      selectedClientId.value = item.telephone
      searchDetailClient.value = item.client
      showDetailDropdown.value = false
      loadDetailClient()
    }

    const clearDetailSelection = () => {
      selectedClientId.value = ''
      searchDetailClient.value = ''
      selectedClient.value = null
      clientCreances.value = []
      clientPaiements.value = []
    }

    const selectPaiementClient = (item) => {
      paiementForm.value.clientId = item.telephone
      searchPaiementClient.value = item.client
      showPaiementDropdown.value = false
      loadCreancesClient()
    }

    const clearPaiementSelection = () => {
      paiementForm.value.clientId = ''
      paiementForm.value.resteAPayer = 0
      paiementForm.value.creanceId = ''
      searchPaiementClient.value = ''
    }

    const handleAction = (creance) => {
      if (creance.actionSelected === 'paiement') {
        openPaiementModal(creance)
      }
      creance.actionSelected = ''
    }

    const openPaiementModal = (creance) => {
      paiementForm.value.clientId = creance.client?.telephone || selectedClient.value?.telephone
      paiementForm.value.clientName = creance.client?.nomComplet || selectedClient.value?.nomComplet
      paiementForm.value.creanceId = creance._id
      paiementForm.value.resteAPayer = creance.resteAPayer
      paiementForm.value.montant = 0
      paiementForm.value.modePaiement = ''
      paiementForm.value.reference = ''
      showPaiementModal.value = true
    }

    const closePaiementModal = () => {
      showPaiementModal.value = false
      showConfirmModal.value = false
      paiementForm.value = {
        clientId: '',
        clientName: '',
        creanceId: '',
        montant: 0,
        modePaiement: '',
        reference: '',
        resteAPayer: 0
      }
    }

    // Afficher le modal de confirmation
    const showConfirmationModal = () => {
      if (!canSubmitPaiement.value) return
      showConfirmModal.value = true
    }

    // Annuler la confirmation
    const cancelConfirmation = () => {
      showConfirmModal.value = false
    }

    // Confirmer et enregistrer le paiement avec statut Validé
    const confirmPaiement = async () => {
      try {
        await api.post(`/creances/${paiementForm.value.creanceId}/paiement`, {
          montant: paiementForm.value.montant,
          modePaiement: paiementForm.value.modePaiement,
          reference: paiementForm.value.reference,
          validerDirectement: true
        })
        
        toast.success('✅ Paiement validé avec succès')
        
        // Fermer les modals et reset form
        closePaiementModal()
        
        // Recharger les données
        loadVueGlobale()
        loadDetailClient()
        
      } catch (error) {
        console.error('Erreur paiement:', error)
        toast.error(error.response?.data?.message || 'Erreur lors du paiement')
      }
    }

    // Ancienne fonction conservée pour compatibilité
    const enregistrerPaiement = async () => {
      showConfirmationModal()
    }

    const sauvegarderNote = async (item) => {
      // TODO: Implémenter la sauvegarde de note
      console.log('Note à sauvegarder:', item.note)
    }

    const validerPaiement = async (creanceId, paiementId) => {
      if (!confirm('Valider ce paiement ? Le reste à payer sera mis à jour.')) return
      
      try {
        await api.put(`/creances/${creanceId}/paiement/${paiementId}/valider`, {
          action: 'valider'
        })
        toast.success('✅ Paiement validé avec succès')
        loadDetailClient()
        loadVueGlobale()
      } catch (error) {
        console.error('Erreur validation paiement:', error)
        toast.error(error.response?.data?.message || 'Erreur lors de la validation')
      }
    }

    const rejeterPaiement = async (creanceId, paiementId) => {
      const commentaire = prompt('Motif du rejet:')
      if (!commentaire) return
      
      try {
        await api.put(`/creances/${creanceId}/paiement/${paiementId}/valider`, {
          action: 'rejeter',
          commentaire
        })
        toast.success('Paiement rejeté')
        loadDetailClient()
        loadVueGlobale()
      } catch (error) {
        console.error('Erreur rejet paiement:', error)
        toast.error(error.response?.data?.message || 'Erreur lors du rejet')
      }
    }

    const getStatutClass = (statut) => {
      if (!statut) return ''
      const s = statut.toLowerCase()
      if (s === 'validé') return 'valide'
      if (s === 'en attente') return 'en-attente'
      if (s === 'rejeté') return 'rejete'
      return s
    }

    // Export Vue Globale en Excel
    const exportVueGlobaleExcel = () => {
      if (filteredCreancesGlobale.value.length === 0) {
        toast.warning('Aucune donnée à exporter')
        return
      }
      
      const headers = ['Client', 'Téléphone', 'Prix Total Opérations', 'Total Avance', 'Reste Créance']
      
      const rows = filteredCreancesGlobale.value.map(c => [
        c.client || '',
        c.telephone || '',
        c.totalOperations || 0,
        c.totalAvance || 0,
        c.totalCreance || 0
      ])
      
      // Ajouter la ligne total
      rows.push([
        'TOTAL',
        '',
        totalOperationsGlobal.value,
        totalAvanceGlobal.value,
        statistiques.value.totalGlobal
      ])
      
      exportToExcel(headers, rows, `Vue_Globale_Creances_${new Date().toISOString().split('T')[0]}`)
      toast.success('✅ Export Excel réussi')
    }

    const formatDate = (date) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      })
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-TN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0) + ' TND'
    }

    const getModeFromRdv = (creance) => {
      return creance.rdv?.encaissement?.modePaiement || 'Espèces'
    }

    // Export Créances en Excel
    const exportCreancesExcel = () => {
      if (clientCreances.value.length === 0) return
      
      const clientName = selectedClient.value?.nomComplet || 'Client'
      const headers = ['Date', 'Technicien', 'Opération', 'Total', 'Avance', 'Date Avance', 'Mode', 'Reste']
      
      const rows = clientCreances.value.map(c => [
        formatDate(c.dateIntervention),
        `${c.technicien?.prenom || ''} ${c.technicien?.nom || ''}`.trim(),
        c.typeIntervention || 'Installation',
        c.prixMission || 0,
        c.montantPaye || 0,
        formatDate(c.dateCreation),
        getModeFromRdv(c),
        c.resteAPayer || 0
      ])
      
      exportToExcel(headers, rows, `Creances_${clientName}_${new Date().toISOString().split('T')[0]}`)
      toast.success('✅ Export Excel créances réussi')
    }

    // Export Paiements en Excel
    const exportPaiementsExcel = () => {
      if (clientPaiements.value.length === 0) return
      
      const clientName = selectedClient.value?.nomComplet || 'Client'
      const headers = ['Date', 'Type', 'Montant', 'Mode', 'Agent', 'Statut']
      
      const rows = clientPaiements.value.map(p => [
        formatDate(p.datePaiement),
        p.isAvanceInitiale ? 'Avance' : 'Paiement',
        p.montant || 0,
        p.modePaiement || '',
        `${p.collectePar?.prenom || ''} ${p.collectePar?.nom || ''}`.trim() || '-',
        p.statut || ''
      ])
      
      exportToExcel(headers, rows, `Paiements_${clientName}_${new Date().toISOString().split('T')[0]}`)
      toast.success('✅ Export Excel paiements réussi')
    }

    // Fonction générique d'export Excel
    const exportToExcel = (headers, rows, filename) => {
      // Créer le contenu CSV avec BOM pour Excel
      const BOM = '\uFEFF'
      const csvContent = BOM + [
        headers.join(';'),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
      ].join('\n')
      
      // Créer et télécharger le fichier
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${filename}.csv`
      link.click()
      URL.revokeObjectURL(link.href)
    }

    onMounted(() => {
      loadVueGlobale()
      
      // Fermer les dropdowns quand on clique ailleurs
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.searchable-select')) {
          showDetailDropdown.value = false
          showPaiementDropdown.value = false
        }
      })
    })

    return {
      loading, activeView, creancesGlobale, filterClient, statistiques,
      nombreClients, totalOperationsGlobal, totalAvanceGlobal, filteredCreancesGlobale,
      selectedClientId, selectedClient, clientCreances, clientPaiements, clientTotaux,
      paiementForm, canSubmitPaiement, showPaiementModal, showConfirmModal,
      searchDetailClient, showDetailDropdown, filteredDetailClients,
      searchPaiementClient, showPaiementDropdown, filteredPaiementClients,
      accordionCreances, accordionPaiements,
      afficherClientsSoldes,
      loadVueGlobale, loadDetailClient, loadCreancesClient, voirDetailClient,
      selectDetailClient, clearDetailSelection, selectPaiementClient, clearPaiementSelection,
      handleAction, openPaiementModal, enregistrerPaiement, closePaiementModal, sauvegarderNote,
      formatDate, formatCurrency, getModeFromRdv,
      validerPaiement, rejeterPaiement, getStatutClass,
      showConfirmationModal, cancelConfirmation, confirmPaiement,
      exportCreancesExcel, exportPaiementsExcel, exportVueGlobaleExcel
    }
  }
}
</script>

<style scoped>
.recouvrement-creances-manager { padding: 1rem; }

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-info h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #f59e0b;
  font-size: 1.25rem;
}

.header-info p {
  margin: 0.25rem 0 0;
  color: #64748b;
  font-size: 0.85rem;
}

.header-stats {
  display: flex;
  gap: 1rem;
}

.stat-card {
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  text-align: center;
}

.stat-card.total {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
}

.stat-card.clients {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
}

.stat-card .stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
}

.stat-card .stat-label {
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
}

/* Tabs */
.view-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 0.5rem;
}

.tab-btn {
  padding: 0.75rem 1.25rem;
  border: none;
  background: transparent;
  color: #64748b;
  font-weight: 500;
  cursor: pointer;
  border-radius: 8px 8px 0 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.tab-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.tab-btn.active {
  background: #f59e0b;
  color: white;
}

/* Filters */
.filters-bar {
  margin-bottom: 1rem;
}

.filter-group {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.filter-input {
  flex: 1;
  min-width: 200px;
  padding: 0.625rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
}

.btn-refresh, .btn-export-global {
  padding: 0.625rem 1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.btn-refresh {
  background: #f1f5f9;
  color: #64748b;
}

.btn-export-global {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: white;
  transition: all 0.2s ease;
}

.btn-export-global:hover {
  background: linear-gradient(135deg, #16a34a, #15803d);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
}

/* Table */
.table-container {
  overflow-x: auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.creances-table, .detail-table {
  width: 100%;
  border-collapse: collapse;
}

.creances-table th, .detail-table th {
  background: #f8fafc;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #475569;
  font-size: 0.85rem;
  text-transform: uppercase;
  border-bottom: 2px solid #e2e8f0;
}

.creances-table td, .detail-table td {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.9rem;
}

.text-right { text-align: right; }
.text-success { color: #10b981; }
.text-danger { color: #ef4444; }
.font-bold { font-weight: 700; }

.client-cell {
  display: flex;
  flex-direction: column;
}

.client-cell .telephone {
  font-size: 0.8rem;
  color: #64748b;
}

.btn-voir {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
}

.note-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.85rem;
}

.total-row {
  background: #fef3c7;
}

.total-row td {
  font-weight: 700;
  border-top: 2px solid #f59e0b;
}

/* Client selector */
.client-selector {
  margin-bottom: 1.5rem;
}

.client-selector label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #475569;
}

/* Bouton Paiement */
.btn-paiement {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-paiement:hover {
  background: linear-gradient(135deg, #059669, #047857);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-paiement i {
  font-size: 0.85rem;
}

.badge-solde {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #047857;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

/* Accordéon Styles */
.accordion-section {
  margin-bottom: 1rem;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
}

.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid transparent;
}

.accordion-header:hover {
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
}

.accordion-header.active {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border-bottom: 1px solid #fcd34d;
}

.accordion-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  color: #1e293b;
  font-size: 1rem;
}

.accordion-title i {
  color: #f59e0b;
  font-size: 1.1rem;
}

.badge-count {
  background: #f59e0b;
  color: white;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
}

.accordion-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-export {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.85rem;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-export:hover {
  background: linear-gradient(135deg, #16a34a, #15803d);
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(34, 197, 94, 0.3);
}

.btn-export i {
  font-size: 0.8rem;
}

.accordion-icon {
  color: #64748b;
  transition: transform 0.3s ease;
}

.accordion-header.active .accordion-icon {
  transform: rotate(180deg);
  color: #f59e0b;
}

.accordion-content {
  padding: 0;
  background: white;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 2000px;
  }
}

/* Searchable Select */
.searchable-select {
  position: relative;
  max-width: 450px;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 0 1rem;
  transition: all 0.2s;
}

.search-input-wrapper:focus-within {
  border-color: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
}

.search-input-wrapper i.fa-search {
  color: #94a3b8;
  margin-right: 0.75rem;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 0.875rem 0;
  font-size: 0.95rem;
  background: transparent;
}

.search-input::placeholder {
  color: #94a3b8;
}

.clear-btn {
  background: #f1f5f9;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s;
}

.clear-btn:hover {
  background: #fee2e2;
  color: #ef4444;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
  max-height: 280px;
  overflow-y: auto;
  z-index: 100;
  margin-top: 4px;
}

.dropdown-item {
  padding: 0.875rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.15s;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
}

.item-name {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.item-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
}

.item-phone {
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.item-amount {
  font-weight: 700;
}

.dropdown-empty {
  padding: 1.5rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.9rem;
}

.client-select {
  width: 100%;
  max-width: 400px;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
}

/* Client detail */
.client-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  padding: 1.25rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.summary-header h4 {
  margin: 0;
  font-size: 1.1rem;
  color: #1e293b;
}

.summary-header p {
  margin: 0.25rem 0 0;
  color: #64748b;
  font-size: 0.85rem;
}

.summary-stats .stat {
  text-align: right;
}

.summary-stats .label {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
}

.summary-stats .value {
  font-size: 1.5rem;
  font-weight: 700;
}

.summary-stats .value.danger { color: #ef4444; }

.detail-table-container {
  overflow-x: auto;
  background: white;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.mode-badge {
  padding: 0.25rem 0.5rem;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 4px;
  font-size: 0.75rem;
}

.action-select {
  padding: 0.35rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.8rem;
}

/* Paiements history table */
.paiements-history {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  margin-top: 1.5rem;
}

.paiements-history h5 {
  margin: 0 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #475569;
  font-size: 1rem;
}

.paiements-table-container {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.paiements-table {
  width: 100%;
  border-collapse: collapse;
}

.paiements-table th {
  background: #f8fafc;
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #475569;
  font-size: 0.8rem;
  text-transform: uppercase;
  border-bottom: 2px solid #e2e8f0;
}

.paiements-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.9rem;
  vertical-align: middle;
}

.paiements-table tbody tr:last-child td {
  border-bottom: none;
}

.paiements-table tbody tr.valide {
  background: #f0fdf4;
}

.paiements-table tbody tr.en-attente {
  background: #fffbeb;
}

.paiements-table tbody tr.rejete {
  background: #fef2f2;
}

.paiements-table tbody tr:hover {
  background: #f1f5f9;
}

.agent-name {
  color: #64748b;
  font-size: 0.85rem;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.type-badge.avance {
  background: #dbeafe;
  color: #1d4ed8;
}

.type-badge.paiement {
  background: #d1fae5;
  color: #059669;
}

.avance-row {
  background: #eff6ff !important;
}

.action-buttons {
  display: flex;
  gap: 0.35rem;
}

.no-action {
  color: #94a3b8;
}

.btn-validate, .btn-reject {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.btn-validate {
  background: #10b981;
  color: white;
}

.btn-validate:hover {
  background: #059669;
  transform: scale(1.1);
}

.btn-reject {
  background: #ef4444;
  color: white;
}

.btn-reject:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.statut-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.statut-badge.valide { background: #10b981; color: white; }
.statut-badge.en-attente { background: #f59e0b; color: white; }
.statut-badge.rejete { background: #ef4444; color: white; }

/* Paiement form */
.paiement-form-container {
  max-width: 500px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.form-header {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  padding: 1.25rem;
}

.form-header h4 {
  margin: 0;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #475569;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
}

.reste-display {
  padding: 0.75rem 1rem;
  background: #fee2e2;
  color: #dc2626;
  font-weight: 700;
  font-size: 1.25rem;
  border-radius: 8px;
  text-align: center;
}

.btn-primary {
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-primary:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

.loading-state {
  text-align: center;
  padding: 3rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #f59e0b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* Modal Paiement */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.paiement-modal {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.paiement-modal .modal-header {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.paiement-modal .modal-header h4 {
  margin: 0;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
}

.paiement-modal .modal-header .close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.paiement-modal .modal-header .close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.paiement-modal .modal-body {
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
}

.client-info-modal {
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 1.5rem;
}

.client-info-modal .client-name {
  font-weight: 600;
  font-size: 1.1rem;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.client-info-modal .client-phone {
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.paiement-modal .modal-footer {
  padding: 1rem 1.5rem;
  background: #f8fafc;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  border-top: 1px solid #e2e8f0;
}

.paiement-modal .btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.paiement-modal .btn-secondary {
  background: #e2e8f0;
  color: #475569;
}

.paiement-modal .btn-secondary:hover {
  background: #cbd5e1;
}

.paiement-modal .btn-primary {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  width: auto;
}

.paiement-modal .btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669, #047857);
  transform: translateY(-1px);
}

.paiement-modal .btn-primary:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-stats {
    width: 100%;
    justify-content: space-between;
  }
  
  .view-tabs {
    flex-wrap: wrap;
  }
  
  .tab-btn {
    flex: 1;
    justify-content: center;
    font-size: 0.85rem;
  }
  
  .client-summary {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .summary-stats .stat {
    text-align: center;
  }
  
  .paiement-modal {
    max-width: 100%;
    margin: 0.5rem;
  }
  
  .paiement-modal .modal-footer {
    flex-direction: column;
  }
  
  .paiement-modal .btn {
    width: 100%;
    justify-content: center;
  }
}

@media print {
  .view-tabs, .filters-bar, .btn-voir, .note-input, .action-select {
    display: none !important;
  }
  
  .table-container {
    box-shadow: none;
  }
}

/* Modal de confirmation personnalisé */
.confirmation-overlay {
  z-index: 1100;
}

.confirmation-modal {
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.confirmation-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirmation-icon i {
  font-size: 2.5rem;
  color: #f59e0b;
}

.confirmation-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1rem;
}

.confirmation-message {
  font-size: 1rem;
  color: #64748b;
  margin: 0 0 1.5rem;
  line-height: 1.6;
}

.confirmation-message .highlight {
  color: #f59e0b;
  font-weight: 600;
}

.confirmation-amount {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
}

.confirmation-amount .amount-label {
  color: #64748b;
  font-size: 0.9rem;
}

.confirmation-amount .amount-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #10b981;
}

.confirmation-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.confirmation-buttons .btn-cancel,
.confirmation-buttons .btn-confirm {
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.confirmation-buttons .btn-cancel {
  background: #f1f5f9;
  color: #64748b;
}

.confirmation-buttons .btn-cancel:hover {
  background: #e2e8f0;
  color: #475569;
}

.confirmation-buttons .btn-confirm {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.confirmation-buttons .btn-confirm:hover {
  background: linear-gradient(135deg, #059669, #047857);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

@media (max-width: 480px) {
  .confirmation-modal {
    padding: 1.5rem;
  }
  
  .confirmation-buttons {
    flex-direction: column;
  }
  
  .confirmation-buttons .btn-cancel,
  .confirmation-buttons .btn-confirm {
    width: 100%;
    justify-content: center;
  }
}

/* Bouton Toggle Clients Soldés */
.btn-toggle-soldes {
  padding: 0.625rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #64748b;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-toggle-soldes:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
  color: #475569;
}

.btn-toggle-soldes.active {
  background: linear-gradient(135deg, #10b981, #059669);
  border-color: #10b981;
  color: white;
}

.btn-toggle-soldes.active:hover {
  background: linear-gradient(135deg, #059669, #047857);
  border-color: #059669;
}

.btn-toggle-soldes i {
  font-size: 0.9rem;
}
</style>
